/**
 * De alertas a **frentes**: la unidad de la Zona 1 del tablero de riesgo.
 *
 * Una alerta de la API dice qué está mal. Un frente agrega las dos cosas que la
 * pantalla necesita para que se pueda accionar hoy: **a qué entidad pertenece**
 * (para saber si ya tiene un seguimiento que lo cubre y para poder crearlo) y
 * **quién es el dueño**. Todo se deriva del payload; no hay estado propio.
 *
 * Puro y sin React: la lógica de qué está roto se prueba sin renderizar nada.
 */
import type {
  Alert,
  EntityType,
  Followup,
  FollowupsByOwner,
  Health,
  Links,
  MetricValue,
} from '@ops/contracts'
import type { DeepLinkPayload } from '@ops/contracts'

export interface Frente {
  id: string
  severidad: Health
  titulo: string
  detalle: string
  /** Entidad sobre la que se abre el seguimiento. */
  entityType: EntityType
  entityId: string
  /** Ruta interna donde se resuelve, tal como la manda la API. */
  href: string
  kind: Alert['kind']
}

/** Cobertura de un frente: el seguimiento que ya existe, con su link a Plane. */
export interface Cobertura {
  followup: Followup
  link: DeepLinkPayload | null
}

/**
 * Entidad de una alerta, deducida de su `href`, que la API arma como
 * `/metricas/{slug}` o `/clientes/{id}`. Se usa el href y no el `id` de la
 * alerta porque el href es parte del contrato y el formato del id no.
 */
export function entidadDeAlerta(alert: Alert): {
  entityType: EntityType
  entityId: string
} {
  const ultimo = alert.href.split('/').filter((p) => p !== '').pop() ?? ''
  return {
    entityType: alert.kind === 'client_risk' ? 'client' : 'metric',
    entityId: ultimo,
  }
}

/**
 * Los dos tipos de alerta que **son deuda de datos** y no un frente de negocio.
 * Se declara una sola vez: `frentesDeAlertas` los excluye y `deuda.ts` los
 * consume. Con dos listas, agregar un kind de deuda dejaba una alerta contada
 * en un lado y dibujada en ninguno.
 */
export const KINDS_DE_DEUDA: readonly Alert['kind'][] = [
  'metric_missing',
  'data_expired',
]

export function esDeudaDeDatos(alert: Alert): boolean {
  return KINDS_DE_DEUDA.includes(alert.kind)
}

/** Severidad del panel a partir de la de la alerta. */
function severidadDe(alert: Alert): Health {
  return alert.severity === 'critical' ? 'critical' : 'warning'
}

/**
 * Frentes de una lista de alertas, filtrados por severidad.
 *
 * La deuda de datos se excluye a propósito: es un frente de primera clase con
 * su propio bloque, y repetirla como una tarjeta más la diluye entre los
 * frentes de negocio.
 *
 * Se excluyen **los dos** tipos de deuda, `metric_missing` y `data_expired`.
 * Antes salía solo el primero, y el resultado era un chip «1 frente en
 * atención» que contaba una alerta `data_expired` que la Zona 1 no dibujaba
 * (solo renderiza las críticas) y que el bloque de deuda tampoco miraba:
 * clickear el chip no llevaba a ninguna parte porque no había a dónde llevar.
 * Ahora esa alerta la cuenta el chip de «cargas vencidas», que sí tiene destino
 * — el bloque de deuda, donde la métrica se ve con su badge y su acción.
 */
export function frentesDeAlertas(
  alerts: readonly Alert[],
  severidad: Alert['severity'],
): Frente[] {
  return alerts
    .filter((a) => a.severity === severidad && !esDeudaDeDatos(a))
    .map((alert) => ({
      id: alert.id,
      severidad: severidadDe(alert),
      titulo: alert.titulo,
      detalle: alert.detalle,
      ...entidadDeAlerta(alert),
      href: alert.href,
      kind: alert.kind,
    }))
}

/** Clave del índice de cobertura. Un frente y su seguimiento comparten entidad. */
export function claveEntidad(entityType: EntityType, entityId: string): string {
  return `${entityType}:${entityId}`
}

/**
 * Índice de seguimientos por entidad, para poder poner "Ya tiene seguimiento:"
 * al lado del frente. Los cancelados no cubren nada: un frente con un
 * seguimiento cancelado está huérfano y tiene que volver a ofrecer crear uno.
 */
export function indiceCobertura(
  grupos: FollowupsByOwner,
  links: Links,
): Map<string, Cobertura> {
  const indice = new Map<string, Cobertura>()

  for (const grupo of grupos) {
    for (const followup of grupo.followups) {
      if (followup.estadoGroup === 'cancelled') continue
      const clave = claveEntidad(followup.entityType, followup.entityId)
      // El primero gana: los grupos llegan ordenados por la API.
      if (indice.has(clave)) continue
      indice.set(clave, {
        followup,
        link: links.followups[followup.id]?.plane ?? null,
      })
    }
  }

  return indice
}

/** Cuántos seguimientos abiertos hay, en todos los grupos. */
export function contarFollowups(grupos: FollowupsByOwner): number {
  return grupos.reduce((total, g) => total + g.followups.length, 0)
}

/**
 * Peor severidad de un conjunto. Es el estado general de la barra de triage: si
 * hay un frente crítico, la empresa está en crítico, aunque el resto esté sano.
 */
const PESO: Record<Health, number> = {
  critical: 3,
  warning: 2,
  unknown: 1,
  ok: 0,
}

export function peorSeveridad(severidades: readonly Health[]): Health {
  return severidades.reduce<Health>(
    (peor, actual) => (PESO[actual] > PESO[peor] ? actual : peor),
    'ok',
  )
}

/** Métricas sanas de la Zona 3: con dato y en target. */
export function metricasSanas(metricas: readonly MetricValue[]): MetricValue[] {
  return metricas.filter((m) => m.valor !== null && m.health === 'ok')
}

/** Métricas de la Zona 2: con dato y en ámbar. Todavía no son un problema. */
export function metricasEnMovimiento(
  metricas: readonly MetricValue[],
): MetricValue[] {
  return metricas.filter((m) => m.valor !== null && m.health === 'warning')
}

/**
 * Carga más reciente del tablero, para la marca temporal de la barra de triage.
 * `null` cuando ninguna métrica tiene carga: entonces la barra no miente con una
 * fecha, dice que no hay.
 */
export function ultimaCarga(metricas: readonly MetricValue[]): string | null {
  let ultima: string | null = null
  for (const m of metricas) {
    const iso = m.provenance.updatedAt
    if (iso === null) continue
    if (ultima === null || iso > ultima) ultima = iso
  }
  return ultima
}
